import { withAlpha, hex, type Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { paintGlyph } from "../themes/glyphRegistry.js";
import type { Theme } from "../themes/Theme.js";

export interface Resource {
  id: string;
  /** Shown before the number when there is no glyph. */
  name?: string;
  /** A name from the glyph registry, drawn as the icon. */
  glyph?: string;
  color?: Color | null;
  amount: number;
  /** A cap, shown as "340/500" and reddened when reached. */
  capacity?: number;
  /** Change per minute, shown as "+12" beside the amount. */
  rate?: number;
}

export interface ResourceBarOptions extends ControlOptions {
  resources?: readonly Resource[];
  font?: FontSpec | null;
  /** Gap between one resource and the next. */
  gap?: number;
  /** Icon side, in px. */
  iconSize?: number;
  /** Seconds a change flashes for. 0 never flashes. */
  flashSeconds?: number;
  showRates?: boolean;
}

const DEFAULT_GAP = 18;
const DEFAULT_ICON = 14;
const DEFAULT_FLASH_S = 0.8;
const ICON_GAP = 5;

const GAIN_COLOR = hex("#63d68a");
const LOSS_COLOR = hex("#e07070");

/**
 * The strip of gold, wood and food across the top of a strategy game.
 *
 * The flash on change is the point of it: a resource that dropped goes red
 * for a beat and one that rose goes green, so the player catches a purchase
 * or a raid out of the corner of their eye without watching the number. Set
 * `flashSeconds` to 0 for a plain readout.
 *
 * Drawn rather than built from labels, for the same reason `QuestTracker` is:
 * these numbers change several times a second and a subtree of controls per
 * resource is a lot of machinery for a string that is about to change again.
 */
export class ResourceBar extends Control {
  #resources: Resource[];
  /** The last change to each resource: when, and by how much. */
  #changes = new Map<string, { at: number; delta: number }>();
  /** What each resource held before that change, to measure the next against. */
  #previous = new Map<string, number>();
  #animating = false;

  font: FontSpec | null;
  gap: number;
  iconSize: number;
  flashSeconds: number;
  showRates: boolean;

  constructor(options: ResourceBarOptions = {}) {
    super({ width: 420, height: 24, tabStop: false, ...options });
    this.#resources = [...(options.resources ?? [])];
    this.font = options.font ?? null;
    this.gap = options.gap ?? DEFAULT_GAP;
    this.iconSize = options.iconSize ?? DEFAULT_ICON;
    this.flashSeconds = options.flashSeconds ?? DEFAULT_FLASH_S;
    this.showRates = options.showRates ?? true;
    for (const r of this.#resources) this.#previous.set(r.id, r.amount);
  }

  get resources(): readonly Resource[] {
    return this.#resources;
  }
  set resources(v: readonly Resource[]) {
    for (const next of v) {
      const before = this.#previous.get(next.id);
      if (before !== undefined && before !== next.amount) {
        this.#changes.set(next.id, {
          at: this.desktop?.frameTime ?? 0,
          delta: next.amount - before,
        });
      }
      this.#previous.set(next.id, next.amount);
    }
    this.#resources = [...v];
    this.#syncAnimation();
    this.emit("change");
    this.invalidate();
  }

  /** Write one resource's amount, flashing it if it moved. */
  setAmount(id: string, amount: number): void {
    const i = this.#resources.findIndex((r) => r.id === id);
    if (i < 0) return;
    const current = this.#resources[i]!;
    if (current.amount === amount) return;
    this.#changes.set(id, {
      at: this.desktop?.frameTime ?? 0,
      delta: amount - current.amount,
    });
    this.#previous.set(id, amount);
    this.#resources[i] = { ...current, amount };
    this.#syncAnimation();
    this.emit("change");
    this.invalidate();
  }

  find(id: string): Resource | null {
    return this.#resources.find((r) => r.id === id) ?? null;
  }

  /** True when the resource is at or past its cap. */
  isCapped(resource: Resource): boolean {
    return resource.capacity !== undefined && resource.amount >= resource.capacity;
  }

  /**
   * How brightly a resource is flashing, and which way, -1..1.
   * Negative for a loss, positive for a gain, 0 for settled.
   */
  flashOf(resource: Resource): number {
    if (this.flashSeconds <= 0) return 0;
    const change = this.#changes.get(resource.id);
    if (!change) return 0;
    const age = (this.desktop?.frameTime ?? 0) - change.at;
    if (age >= this.flashSeconds) return 0;
    // The delta is recorded at the moment of the change rather than derived
    // here, because by the time this runs the old amount is long gone.
    return (1 - age / this.flashSeconds) * (change.delta >= 0 ? 1 : -1);
  }

  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.flashSeconds > 0 && this.#changes.size > 0;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  protected override onEnvironmentChanged(): void {
    this.#animating = false;
    this.#syncAnimation();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    theme.drawResourceBackground(rc, this.localRect);

    const p = theme.palette;
    const font = this.font ?? theme.metrics.font;
    const middle = this.height / 2;
    let x = 0;
    let anyFlashing = false;

    for (const resource of this.#resources) {
      const tint = resource.color ?? p.text;
      if (resource.glyph) {
        const box = { x, y: middle - this.iconSize / 2, width: this.iconSize, height: this.iconSize };
        paintGlyph(resource.glyph, { rc, box, ink: tint, weight: 2 });
        x += this.iconSize + ICON_GAP;
      } else if (resource.name) {
        const label = `${resource.name} `;
        rc.drawText(label, x, middle, font, p.textSubtle, { baseline: "middle" });
        x += rc.measureText(label, font).width;
      }

      const flash = this.flashOf(resource);
      if (flash !== 0) anyFlashing = true;
      const capped = this.isCapped(resource);
      let ink = capped ? LOSS_COLOR : tint;
      if (flash !== 0) {
        // Blended towards green or red by how fresh the change is, rather than
        // swapped outright: a hard color change flickers, a blend reads.
        const towards = flash > 0 ? GAIN_COLOR : LOSS_COLOR;
        ink = blend(ink, towards, Math.abs(flash));
      }

      const text =
        resource.capacity !== undefined
          ? `${format(resource.amount)}/${format(resource.capacity)}`
          : format(resource.amount);
      rc.drawText(text, x, middle, font, ink, { baseline: "middle" });
      x += rc.measureText(text, font).width;

      if (this.showRates && resource.rate !== undefined && resource.rate !== 0) {
        const rate = ` ${resource.rate > 0 ? "+" : ""}${format(resource.rate)}`;
        const rateInk = withAlpha(resource.rate > 0 ? GAIN_COLOR : LOSS_COLOR, 0.85);
        rc.drawText(rate, x, middle, font, rateInk, { baseline: "middle" });
        x += rc.measureText(rate, font).width;
      }

      x += this.gap;
    }

    // Stop asking for frames once every flash has burned out.
    if (!anyFlashing && this.#changes.size > 0) {
      this.#changes.clear();
      this.#syncAnimation();
    }
  }
}

/** "1.2k", "3.4M" — a resource readout has to fit in a strip. */
export function format(amount: number): string {
  const sign = amount < 0 ? "-" : "";
  const n = Math.abs(amount);
  if (n >= 1_000_000) return `${sign}${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${sign}${(n / 1000).toFixed(1)}k`;
  return `${sign}${Math.round(n)}`;
}

/** Mix `to` into `from` by `t`, 0..1. */
function blend(from: Color, to: Color, t: number): Color {
  return {
    r: from.r + (to.r - from.r) * t,
    g: from.g + (to.g - from.g) * t,
    b: from.b + (to.b - from.b) * t,
    a: from.a + (to.a - from.a) * t,
  };
}
